/// <reference types="./common.d.mts" />
import * as $when from "../../../app_config/app_config/when.mjs";
import * as $bright from "../../../bright/bright.mjs";
import * as $data_point from "../../../common/data/data_point.mjs";
import * as $data_source from "../../../common/data/data_source.mjs";
import * as $language from "../../../common/data/translate/language.mjs";
import * as $user from "../../../common/data/user.mjs";
import * as $colors from "../../../ds/ds/colors.mjs";
import * as $disclosure from "../../../ds/ds/components/disclosure.mjs";
import * as $l from "../../../ds/ds/components/layout.mjs";
import * as $portal from "../../../ds/ds/components/portal.mjs";
import * as $pp from "../../../ds/ds/components/profile_picture.mjs";
import * as $icons from "../../../ds/ds/icons.mjs";
import * as $position from "../../../ds/ds/internals/position.mjs";
import * as $text from "../../../ds/ds/typography/text.mjs";
import * as $ds_utils from "../../../ds/ds/utils.mjs";
import * as $bool from "../../../gleam_stdlib/gleam/bool.mjs";
import * as $decode from "../../../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $int from "../../../gleam_stdlib/gleam/int.mjs";
import * as $list from "../../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../../../gleam_stdlib/gleam/pair.mjs";
import * as $a from "../../../lustre/lustre/attribute.mjs";
import * as $element from "../../../lustre/lustre/element.mjs";
import * as $event from "../../../lustre/lustre/event.mjs";
import * as $mime_types from "../../../mime_types/mime_types.mjs";
import * as $css from "../../../sketch/sketch/css.mjs";
import * as $length from "../../../sketch/sketch/css/length.mjs";
import { percent, px } from "../../../sketch/sketch/css/length.mjs";
import * as $el from "../../../sketch_lustre/sketch/lustre/element.mjs";
import * as $h from "../../../sketch_lustre/sketch/lustre/element/html.mjs";
import * as $model from "../../data/model.mjs";
import * as $msg from "../../data/msg.mjs";
import * as $translated_text from "../../frontend/view/components/translated_text.mjs";
import { Ok, Error, toList } from "../../gleam.mjs";

export function separator() {
  let _pipe = $css.class$(
    toList([
      $css.width(percent(100)),
      $css.height(px(1)),
      $css.background($colors.light.grey_400),
    ]),
  );
  return $h.hr(_pipe, toList([]));
}

export function row(attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.flex_direction("row"),
      $css.display("flex"),
      $css.gap(px(24)),
      $css.white_space("nowrap"),
    ]),
  );
  return $h.div(_pipe, attrs, children);
}

export function name_small(attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.compose($text.sm($text.regular)),
      $css.display("flex"),
      $css.align_items("center"),
      $css.gap(px(8)),
      $css.white_space("nowrap"),
    ]),
  );
  return $h.div(_pipe, attrs, children);
}

export function underlined_title(attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.compose($text.xl($text.medium)),
      $css.display("flex"),
      $css.flex_direction("column"),
      $css.gap(px(16)),
    ]),
  );
  return $h.div(_pipe, attrs, children);
}

export function card() {
  return $css.class$(
    toList([
      $css.border("1px solid " + $colors.light.grey_400),
      $css.border_radius(px(8)),
      $css.padding(px(32)),
    ]),
  );
}

function stacked_pictures(size, children) {
  let _pipe = $css.class$(
    toList([
      $css.display("grid"),
      $css.grid_template_columns(
        ("repeat(" + $int.to_string(size)) + ", 12px) 12px",
      ),
      $css.transition("grid-template-columns .2s"),
      $css.hover(
        toList([
          $css.grid_template_columns(
            ("repeat(" + $int.to_string(size)) + ", 24px) 0px",
          ),
        ]),
      ),
    ]),
  );
  return $h.div(_pipe, toList([]), children);
}

export function stacked_avatars(users_id, users) {
  let firsts = $list.take(users_id, 4);
  let first_size = $list.length(firsts);
  let rest = $list.length($list.drop(users_id, 4));
  return $l.row(
    toList([$l.align("center"), $l.gap(8)]),
    toList([]),
    toList([
      stacked_pictures(
        first_size,
        $list.index_map(
          firsts,
          (user_id, index) => {
            let user = $list.find(users, (u) => { return u.id === user_id; });
            return $when.ok(
              user,
              (user) => {
                let start = $int.to_string(index + 1);
                let end = $int.to_string(index + 3);
                let col = (start + " / ") + end;
                return $h.div_(
                  toList([
                    $a.style("grid-row", "1"),
                    $a.style("grid-column", col),
                  ]),
                  toList([
                    $pp.from_user(user.picture, user.initials, new $pp.Medium()),
                  ]),
                );
              },
            );
          },
        ),
      ),
      (() => {
        if (rest === 0) {
          return $el.none();
        } else {
          let rest$1 = rest;
          return $h.text("+" + $int.to_string(rest$1));
        }
      })(),
    ]),
  );
}

export function user_info(attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.compose($text.sm($text.medium)),
      $css.color($colors.light.grey_800),
      $css.border("1px solid " + $colors.light.grey_200),
      $css.border_radius(px(10)),
      $css.display("flex"),
      $css.gap(px(16)),
      $css.padding_("4px 4px 4px 16px"),
      $css.align_items("center"),
    ]),
  );
  return $h.span(_pipe, attrs, children);
}

function on_expanded(rect, tooltip_id, point) {
  return $disclosure.on_expanded(
    (value) => {
      return new $msg.UserClickedDisclosureGroup(
        (() => {
          if (value) {
            return new $option.Some([tooltip_id, point.id, rect]);
          } else {
            return new $option.None();
          }
        })(),
      );
    },
  );
}

function on_index_click(model, tooltip_id, point) {
  let state = $bright.state(model);
  return $event.on(
    "click",
    $decode.then$(
      $decode.dynamic,
      (event) => {
        let $ = $position.get_event_bounding_client_rect(event);
        if ($ instanceof Ok) {
          let rect = $[0];
          let _pipe = new $msg.UserClickedDisclosureGroup(
            (() => {
              let $1 = state.sources_tooltip;
              if ($1 instanceof $option.Some) {
                let id = $1[0][0];
                let pid = $1[0][1];
                if ((id === tooltip_id) && (pid === point.id)) {
                  return new $option.None();
                } else {
                  return new $option.Some([tooltip_id, point.id, rect]);
                }
              } else {
                return new $option.Some([tooltip_id, point.id, rect]);
              }
            })(),
          );
          return $decode.success(_pipe);
        } else {
          return $decode.failure(new $msg.UserDisconnected(), "Bounding Rect");
        }
      },
    ),
  );
}

function question_source_title(data_points) {
  return $h.div(
    $css.class$(toList([$css.text_transform("uppercase")])),
    toList([]),
    (() => {
      let $ = $list.is_empty(data_points);
      if ($) {
        return toList([$el.text("No sources")]);
      } else {
        return toList([$el.text("Sources")]);
      }
    })(),
  );
}

function question_source_index_class() {
  return $css.class$(
    toList([$css.display("flex"), $css.align_items("center"), $css.gap(px(8))]),
  );
}

function question_source_index(opened, attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.compose($text.sm($text.medium)),
      $css.color(
        (() => {
          if (opened) {
            return $colors.light.text_display;
          } else {
            return $colors.light.grey_800;
          }
        })(),
      ),
      $css.padding_("4px 8px"),
      $css.background(
        (() => {
          if (opened) {
            return $colors.light.grey_200;
          } else {
            return $colors.light.grey_50;
          }
        })(),
      ),
      $css.transition("background .3s, color .3s"),
      $css.border_radius(px(8)),
      $css.hover(
        toList([
          $css.color($colors.light.text_display),
          $css.background($colors.light.grey_200),
          $css.cursor("default"),
        ]),
      ),
    ]),
  );
  return $h.div(_pipe, attrs, children);
}

export function sources_row(
  model,
  tooltip_id,
  language,
  data_points,
  data_sources
) {
  let state = $bright.state(model);
  let _block;
  {
    let data_points$1 = $list.index_map(data_points, $pair.new$);
    _block = $list.filter_map(
      data_points$1,
      (_use0) => {
        let point = _use0[0];
        let index = _use0[1];
        return $list.find_map(
          data_sources,
          (source) => {
            let is_same_source = source.id === point.source_id;
            return $bool.guard(
              !is_same_source,
              new Error(undefined),
              () => {
                let index$1 = $h.text($int.to_string(index + 1));
                return new Ok([index$1, source, point]);
              },
            );
          },
        );
      },
    );
  }
  let sources = _block;
  return $l.row(
    toList([$l.align("center"), $l.gap(8), $l.position("relative")]),
    toList([]),
    toList([
      question_source_title(data_points),
      $position.wrapper(
        question_source_index_class(),
        toList([]),
        $bool.guard(
          $list.is_empty(sources),
          toList([$el.none()]),
          () => {
            return $list.map(
              sources,
              (_use0) => {
                let index = _use0[0];
                let point = _use0[2];
                let on_index_click$1 = on_index_click(model, tooltip_id, point);
                let _block$1;
                let $ = state.sources_tooltip;
                if ($ instanceof $option.Some) {
                  let id = $[0][0];
                  let pid = $[0][1];
                  if ((pid === point.id) && (id === tooltip_id)) {
                    _block$1 = true;
                  } else {
                    _block$1 = false;
                  }
                } else {
                  _block$1 = false;
                }
                let opened = _block$1;
                return question_source_index(
                  opened,
                  toList([on_index_click$1]),
                  toList([index]),
                );
              },
            );
          },
        ),
      ),
      (() => {
        let $ = state.sources_tooltip;
        if ($ instanceof $option.Some) {
          let id = $[0][0];
          if (id !== tooltip_id) {
            return $el.none();
          } else {
            let expanded = $[0][1];
            let rect = $[0][2];
            let bottom = $position.compute_vertical_position(rect);
            let left = $position.compute_horizontal_position(
              rect,
              new $position.Right(),
            );
            let fixed = $css.position("fixed");
            let z_index = $css.z_index(500_000_000);
            let max_width = $css.max_width(px(400));
            let max_height = $position.compute_max_height(
              rect,
              state.window_dimensions[1],
            );
            let class$ = $css.class$(
              toList([bottom, left, fixed, max_width, max_height, z_index]),
            );
            return $portal.portal(
              10_000_000,
              $disclosure.group(
                class$,
                toList([$ds_utils.stop_propagation()]),
                $list.map(
                  sources,
                  (_use0) => {
                    let source = _use0[1];
                    let point = _use0[2];
                    let icon = $ds_utils.select_icon(
                      source.display.mime_type,
                      source.kind,
                    );
                    let html = $translated_text.translated_data_point(
                      point,
                      state,
                      language,
                    );
                    let is_expanded = point.id === expanded;
                    let expanded$1 = $disclosure.expanded(is_expanded);
                    let disabled = $disclosure.disabled(is_expanded);
                    let on_expanded$1 = on_expanded(rect, tooltip_id, point);
                    return $disclosure.disclosure(
                      toList([expanded$1, on_expanded$1, disabled]),
                      toList([
                        $disclosure.heading(
                          toList([]),
                          toList([
                            $disclosure.summary(
                              toList([]),
                              toList([
                                $icons.small(icon),
                                (() => {
                                  let $1 = source.display.mime_type;
                                  if ($1 instanceof $mime_types.Qna) {
                                    return $h.text("Question bank");
                                  } else if ($1 instanceof $mime_types.Proposal) {
                                    return $h.text("Previous Steerlab proposal");
                                  } else {
                                    return $h.text(source.display.name);
                                  }
                                })(),
                              ]),
                            ),
                            $disclosure.marker(),
                          ]),
                        ),
                        $disclosure.panel(
                          toList([]),
                          toList([
                            $element.unsafe_raw_html(
                              "",
                              "div",
                              toList([]),
                              html,
                            ),
                          ]),
                        ),
                      ]),
                    );
                  },
                ),
              ),
            );
          }
        } else {
          return $el.none();
        }
      })(),
    ]),
  );
}

export function change_button(attrs, children) {
  let _pipe = $css.class$(
    toList([
      $css.display("flex"),
      $css.gap(px(8)),
      $css.align_items("center"),
      $css.cursor("pointer"),
      $css.hover(toList([$css.background($colors.light.grey_50)])),
      $css.padding(px(6)),
      $css.border_radius(px(4)),
      $css.position("relative"),
    ]),
  );
  return $h.button(_pipe, attrs, children);
}

export function selection_bar_icon(icon) {
  let _pipe = $css.class$(toList([$css.width(px(20)), $css.height(px(20))]));
  return $h.div(_pipe, toList([]), toList([icon]));
}

export function select_all_underline(attrs, children) {
  return $h.div(
    $css.class$(toList([$css.text_decoration("underline")])),
    attrs,
    children,
  );
}

export function select_all(all_selected, size, select_all_sources) {
  let _block;
  if (all_selected) {
    _block = [$icons.double_check_in_a_box(), "All", "Deselect all"];
  } else {
    _block = [$icons.check_in_a_box(), $int.to_string(size), "Select all"];
  }
  let $ = _block;
  let select_all_icon = $[0];
  let select_all_count = $[1];
  let select_all_text = $[2];
  return $l.row(
    toList([$l.gap(16), $l.align("center")]),
    toList([]),
    toList([
      $l.row(
        toList([$l.gap(4), $l.padding(6), $l.align("center")]),
        toList([]),
        toList([
          selection_bar_icon(select_all_icon),
          $h.text(("(" + select_all_count) + ")"),
        ]),
      ),
      change_button(
        toList([select_all_sources]),
        toList([
          select_all_underline(toList([]), toList([$h.text(select_all_text)])),
        ]),
      ),
    ]),
  );
}
